import { useCallback, useEffect, useState } from 'react';
import type { MessageSummary } from 'zitimail-shared-types';

import { mail } from '../api/mail';
import { displayName, listDate } from './format';

const key = (m: MessageSummary) => `${m.folder}\u0000${m.uid}`;
const folderLabel = (path: string) => (path === 'INBOX' ? 'Inbox' : path);

/**
 * Mail deleted for good — Trash emptied, or deleted from Trash — that the
 * server still keeps for a while (ADR-0032). Recovering puts a message back
 * in the folder it was deleted from, or the Inbox if that was Trash or Junk.
 */
export function RecoverDeleted({ onBack }: { onBack: () => void }): JSX.Element {
  const [messages, setMessages] = useState<readonly MessageSummary[] | null>(null);
  const [more, setMore] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(() => {
    mail
      .deleted()
      .then((found) => {
        setMessages(found.messages);
        setMore(found.more);
      })
      .catch((e: unknown) =>
        setNote({ ok: false, text: e instanceof Error ? e.message : 'Could not load.' }),
      );
  }, []);
  useEffect(load, [load]);

  const recover = (chosen: readonly MessageSummary[]) => {
    setBusy(true);
    setNote(null);
    mail
      .restoreDeleted({ items: chosen.map((m) => ({ folder: m.folder, uid: m.uid })) })
      .then(({ restored, into }) => {
        setNote({
          ok: true,
          text: `${restored === 1 ? 'Recovered 1 message' : `Recovered ${restored} messages`} into ${into
            .map(folderLabel)
            .join(', ')}.`,
        });
        setSelected(new Set());
        load();
      })
      .catch((e: unknown) =>
        setNote({ ok: false, text: e instanceof Error ? e.message : 'Not recovered.' }),
      )
      .finally(() => setBusy(false));
  };

  const toggle = (m: MessageSummary) => {
    const next = new Set(selected);
    if (next.has(key(m))) {
      next.delete(key(m));
    } else {
      next.add(key(m));
    }
    setSelected(next);
  };
  const chosen = (messages ?? []).filter((m) => selected.has(key(m)));

  return (
    <main className="main settings recover">
      <div className="main-head">
        <button type="button" className="btn ghost" style={{ width: 'auto' }} onClick={onBack}>
          ← Mail
        </button>
        <h2>Recover deleted mail</h2>
      </div>
      <p className="muted">
        Mail you deleted from Trash, or that went when Trash was emptied, is kept here for a while
        (30 days unless your organisation chose otherwise). Recovering puts it back where it was
        deleted from; mail deleted from Trash or Junk goes to your Inbox.
      </p>

      {messages === null && !note && <p>Loading…</p>}
      {messages?.length === 0 && <p className="muted">Nothing deleted to recover.</p>}

      {messages && messages.length > 0 && (
        <div className="save-row">
          <button
            type="button"
            className="btn"
            style={{ width: 'auto' }}
            disabled={busy || chosen.length === 0}
            onClick={() => recover(chosen)}
          >
            {chosen.length > 1 ? `Recover ${chosen.length} messages` : 'Recover selected'}
          </button>
          {more && <span className="muted">The newest {messages.length} are shown.</span>}
        </div>
      )}
      {note && (
        <p className={note.ok ? 'ok' : 'error'} role="status">
          {note.text}
        </p>
      )}

      {messages && messages.length > 0 && (
        <ul className="recover-list" aria-label="Deleted mail">
          {messages.map((m) => (
            <li key={key(m)}>
              <label className="check">
                <input
                  type="checkbox"
                  checked={selected.has(key(m))}
                  onChange={() => toggle(m)}
                  aria-label={`Select ${m.subject || '(no subject)'}`}
                />
                <span className="recover-from">{displayName(m.from)}</span>
                <span className="recover-subject">
                  <strong>{m.subject || '(no subject)'}</strong>
                  {m.preview && <span className="muted"> — {m.preview}</span>}
                </span>
                <span className="muted recover-meta">
                  {listDate(m.date)}, deleted from {folderLabel(m.folder)}
                </span>
              </label>
              <button
                type="button"
                className="btn ghost"
                style={{ width: 'auto' }}
                disabled={busy}
                onClick={() => recover([m])}
                aria-label={`Recover ${m.subject || '(no subject)'}`}
              >
                Recover
              </button>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
